import intl from "react-intl-universal";
import {
  formatFilterDate,
  endOfDay,
  startOfDay,
} from "../../../../Components/Molecules/GridHistory/gridFilterUtils";
import {
  BAAS_ACCOUNT_CURRENCIES,
  BAAS_ACCOUNT_LIST_STATUSES,
  BAAS_ACCOUNT_LIST_TYPES,
} from "../../../../Services/baasAccountsListApi";

export const EMPTY_BAAS_ACCOUNTS_FILTERS = {
  searchQuery: "",
  dateFrom: null,
  dateTo: null,
  statuses: [],
  types: [],
  currencies: [],
  entityIds: [],
  multiCurrencyOnly: false,
  sortByEntity: false,
};

export const getBaasAccountStatusOptions = () =>
  BAAS_ACCOUNT_LIST_STATUSES.map((status) => ({
    id: status,
    label: intl.get(`baas.accounts.status.${status}`),
  }));

export const getBaasAccountTypeOptions = () =>
  BAAS_ACCOUNT_LIST_TYPES
    // OTC is internal — shown as WALLET; filter via wallet option.
    .filter((type) => type !== "otc")
    .map((type) => ({
      id: type,
      label: intl.get(`baas.accounts.type.${type}`),
    }));

export const getBaasAccountCurrencyOptions = () =>
  BAAS_ACCOUNT_CURRENCIES.map((currency) => ({
    id: currency,
    label: currency,
  }));

export const getBaasAccountEntityOptions = (entitiesOrAccounts = []) => {
  const source = Array.isArray(entitiesOrAccounts) ? entitiesOrAccounts : [];
  const seen = new Map();

  source.forEach((item) => {
    if (!item) return;

    if (item.entityId) {
      if (!seen.has(item.entityId)) {
        seen.set(item.entityId, item.entityName || item.entityId);
      }
      return;
    }

    if (item.id) {
      seen.set(item.id, item.name || item.id);
    }
  });

  return Array.from(seen.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const matchesSearch = (account, query) => {
  if (!query?.trim()) return true;
  const normalized = query.trim().toLowerCase();
  return [
    account.accountNumber,
    account.alias,
    account.entityName,
    account.entityId,
    account.id,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalized));
};

const matchesDateRange = (account, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  const createdAt = new Date(account.createdAt);
  if (Number.isNaN(createdAt.getTime())) return false;
  if (dateFrom && createdAt < startOfDay(dateFrom)) return false;
  if (dateTo && createdAt > endOfDay(dateTo)) return false;
  return true;
};

const matchesCurrency = (account, currencies) => {
  if (!currencies?.length) return true;
  const accountCurrencies = (account.balances ?? []).map((balance) =>
    String(balance.currency).toUpperCase(),
  );
  return currencies.some((currency) => accountCurrencies.includes(currency));
};

export const filterBaasAccounts = (items = [], filters = EMPTY_BAAS_ACCOUNTS_FILTERS) => {
  const filtered = items.filter((account) => {
    if (!matchesSearch(account, filters.searchQuery)) return false;
    if (!matchesDateRange(account, filters.dateFrom, filters.dateTo)) return false;
    if (filters.statuses?.length && !filters.statuses.includes(account.status)) return false;
    if (filters.types?.length) {
      const selected = new Set(filters.types);
      // Selecting WALLET also matches internal OTC exit-rail virtuals.
      if (selected.has("wallet")) {
        selected.add("otc");
        selected.add("otc_virtual");
      }
      if (!selected.has(account.type)) return false;
    }
    if (!matchesCurrency(account, filters.currencies)) return false;
    if (filters.entityIds?.length && !filters.entityIds.includes(account.entityId)) return false;
    if (filters.multiCurrencyOnly && (account.balances?.length ?? 0) <= 1) return false;
    return true;
  });

  if (filters.sortByEntity) {
    return [...filtered].sort((a, b) => {
      const entityCompare = a.entityName.localeCompare(b.entityName);
      if (entityCompare !== 0) return entityCompare;
      return a.alias.localeCompare(b.alias);
    });
  }

  return filtered;
};

export const hasActiveBaasAccountsFilters = (filters = EMPTY_BAAS_ACCOUNTS_FILTERS) =>
  Boolean(
    filters.searchQuery?.trim() ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.statuses?.length ||
      filters.types?.length ||
      filters.currencies?.length ||
      filters.entityIds?.length ||
      filters.multiCurrencyOnly ||
      filters.sortByEntity,
  );

export const isBaasAccountsChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "status":
      return Boolean(filters.statuses?.length);
    case "type":
      return Boolean(filters.types?.length);
    case "currency":
      return Boolean(filters.currencies?.length);
    case "entity":
      return Boolean(filters.entityIds?.length);
    default:
      return false;
  }
};

export const buildBaasAccountsFilterTags = (
  filters = EMPTY_BAAS_ACCOUNTS_FILTERS,
  entitiesOrAccounts = [],
) => {
  const tags = [];
  const statusOptions = getBaasAccountStatusOptions();
  const typeOptions = getBaasAccountTypeOptions();
  const currencyOptions = getBaasAccountCurrencyOptions();
  const entityOptions = getBaasAccountEntityOptions(entitiesOrAccounts);

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    const fromLabel = filters.dateFrom ? formatFilterDate(filters.dateFrom) : "…";
    const toLabel = filters.dateTo ? formatFilterDate(filters.dateTo) : "…";
    tags.push({
      key: "date",
      iconType: "date",
      value: `${fromLabel} – ${toLabel}`,
    });
  }

  if (filters.statuses?.length) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((statusId) => statusOptions.find((item) => item.id === statusId)?.label ?? statusId)
        .join(", "),
    });
  }

  if (filters.types?.length) {
    tags.push({
      key: "types",
      iconType: "type",
      value: filters.types
        .map((typeId) => typeOptions.find((item) => item.id === typeId)?.label ?? typeId)
        .join(", "),
    });
  }

  if (filters.currencies?.length) {
    tags.push({
      key: "currencies",
      iconType: "currency",
      value: filters.currencies
        .map(
          (currencyId) =>
            currencyOptions.find((item) => item.id === currencyId)?.label ?? currencyId,
        )
        .join(", "),
    });
  }

  if (filters.entityIds?.length) {
    tags.push({
      key: "entityIds",
      iconType: "subAccount",
      value: filters.entityIds
        .map((entityId) => entityOptions.find((item) => item.id === entityId)?.label ?? entityId)
        .join(", "),
    });
  }

  if (filters.multiCurrencyOnly) {
    tags.push({
      key: "multiCurrencyOnly",
      iconType: "currency",
      value: intl.get("baas.accounts.quickActions.multiCurrency"),
    });
  }

  if (filters.sortByEntity) {
    tags.push({
      key: "sortByEntity",
      iconType: "subAccount",
      value: intl.get("baas.accounts.quickActions.byEntity"),
    });
  }

  return tags;
};

export const removeBaasAccountsFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    case "date":
      return { ...filters, dateFrom: null, dateTo: null };
    case "statuses":
      return { ...filters, statuses: [] };
    case "types":
      return { ...filters, types: [] };
    case "currencies":
      return { ...filters, currencies: [] };
    case "entityIds":
      return { ...filters, entityIds: [] };
    case "multiCurrencyOnly":
      return { ...filters, multiCurrencyOnly: false };
    case "sortByEntity":
      return { ...filters, sortByEntity: false };
    default:
      return filters;
  }
};
